import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react'
import { useJsApiLoader } from '@react-google-maps/api'

interface GoogleMapsContextType {
  isLoaded: boolean
  loadError: Error | undefined
  loadMaps: () => void
}

const GoogleMapsContext = createContext<GoogleMapsContextType | undefined>(undefined)

const LIBRARIES: ('geocoding' | 'streetView' | 'geometry')[] = ['geocoding', 'streetView', 'geometry']

function GoogleMapsLoader({
  onLoaded
}: {
  onLoaded: (result: { isLoaded: boolean; loadError: Error | undefined }) => void
}) {
  const result = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES
  })

  useEffect(() => {
    onLoaded(result)
  }, [result.isLoaded, result.loadError])

  return null
}

export const GoogleMapsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [shouldLoad, setShouldLoad] = useState(false)
  const [state, setState] = useState<{ isLoaded: boolean; loadError: Error | undefined }>({
    isLoaded: false,
    loadError: undefined
  })

  const loadMaps = useCallback(() => setShouldLoad(true), [])

  const value = useMemo(() => ({ ...state, loadMaps }), [state, loadMaps])

  return (
    <GoogleMapsContext.Provider value={value}>
      {shouldLoad && <GoogleMapsLoader onLoaded={setState} />}
      {children}
    </GoogleMapsContext.Provider>
  )
}

export const useGoogleMaps = () => {
  const context = useContext(GoogleMapsContext)
  if (!context) {
    throw new Error('useGoogleMaps must be used within a GoogleMapsProvider')
  }

  // Auto-trigger load when a consumer mounts
  useEffect(() => {
    if (!context.isLoaded) {
      context.loadMaps()
    }
  }, [])

  return context
}
